import React, { useEffect, useState } from "react";
import { Bell, Calendar, Pill, WarningCircle, X } from "@phosphor-icons/react";
import { listAppointments, listPrescriptions } from "@/lib/api";
import { apptDate, apptStatus, rxStatus, rxNextRefillDate, formatRelativeDay, isStaleEvidence } from "@/lib/reminderUtils";
import { VIEWS } from "@/lib/navConfig";

export default function ReminderBanner({ evidence = [], setActiveView }) {
  const [appts, setAppts] = useState([]);
  const [rx, setRx] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [a, r] = await Promise.all([listAppointments(), listPrescriptions()]);
      setAppts(a); setRx(r); setLoaded(true);
    })();
  }, []);

  if (dismissed || !loaded) return null;

  const upcomingAppts = appts.filter((a) => {
    const status = apptStatus(a);
    return !a.completed && (status === "due_soon" || status === "overdue");
  });
  const refillsDue = rx.filter((r) => !r.archived && ["overdue", "due_soon"].includes(rxStatus(r)));
  const staleEv = evidence.filter((ev) => isStaleEvidence(ev));

  const total = upcomingAppts.length + refillsDue.length + staleEv.length;
  if (total === 0) return null;

  return (
    <div className="reminder-banner" data-testid="reminder-banner">
      <div className="reminder-banner-icon"><Bell size={18} weight="fill" /></div>
      <div className="reminder-banner-body">
        <div className="reminder-banner-title">
          {total} item{total === 1 ? "" : "s"} need{total === 1 ? "s" : ""} attention
        </div>
        <div className="reminder-banner-items">
          {upcomingAppts.slice(0, 3).map((a) => {
            const d = apptDate(a);
            return (
              <button
                key={a.id}
                className="reminder-banner-item"
                onClick={() => setActiveView && setActiveView(VIEWS.appointments)}
                data-testid={`banner-appt-${a.id}`}
              >
                <Calendar size={11} weight="bold" />
                <span><strong>{a.title}</strong> — {d ? formatRelativeDay(d) : ""}</span>
              </button>
            );
          })}
          {refillsDue.slice(0, 3).map((r) => {
            const next = rxNextRefillDate(r);
            return (
              <button
                key={r.id}
                className="reminder-banner-item"
                onClick={() => setActiveView && setActiveView(VIEWS.prescriptions)}
                data-testid={`banner-rx-${r.id}`}
              >
                <Pill size={11} weight="bold" />
                <span><strong>{r.medName}</strong> refill — {next ? formatRelativeDay(next) : "soon"}</span>
              </button>
            );
          })}
          {staleEv.length > 0 && (
            <button
              className="reminder-banner-item"
              onClick={() => setActiveView && setActiveView(VIEWS.tracker)}
              data-testid="banner-stale-evidence"
            >
              <WarningCircle size={11} weight="bold" />
              <span><strong>{staleEv.length}</strong> evidence item{staleEv.length === 1 ? "" : "s"} older than 6 months — consider refreshing</span>
            </button>
          )}
        </div>
      </div>
      <button className="reminder-banner-close" onClick={() => setDismissed(true)} data-testid="dismiss-reminder-banner" title="Dismiss until next reload">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
